import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BRAND } from '@saesak/shared';
import { apiData } from '@/lib/api';
import type { SiteInfo } from '@/types';
import { SiteFooter } from './SiteFooter';

/**
 * 개인정보 처리방침 — 공개 페이지 (§9.1).
 * 로그인 없이 접근 가능해야 한다(카카오·스토어·본인인증(PG) 심사가 이 URL을 확인한다).
 * 보호책임자는 관리자 콘솔(정산 탭 공급자 정보)에서 등록한 값이 표시된다 — 배포 없이 관리.
 */

const EFFECTIVE_DATE = '2026년 8월 2일';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="text-lg font-bold">{title}</h2>
      <div className="mt-2 flex flex-col gap-2 text-base leading-relaxed text-ink">{children}</div>
    </section>
  );
}

function Table({ head, rows }: { head: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto rounded-md border border-line-strong">
      <table className="w-full min-w-[480px] text-sm">
        <thead>
          <tr className="bg-primary-soft text-left">
            {head.map((cell) => (
              <th key={cell} className="px-3 py-2 font-semibold">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className="border-t border-line">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="px-3 py-2 align-top">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PrivacyPage() {
  const { data: siteInfo } = useQuery({
    queryKey: ['site-info'],
    queryFn: () => apiData<SiteInfo>('/site-info'),
    staleTime: 5 * 60_000,
  });
  const officer = siteInfo?.privacyOfficer;

  return (
    <main className="mx-auto w-full max-w-screen-sm px-6 py-10">
      <div className="mb-8 flex flex-col items-center gap-2 text-center">
        <img src="/brand-icon.svg" alt="" width={56} height={56} />
        <h1 className="text-2xl font-bold">개인정보 처리방침</h1>
        <p className="text-sm text-ink-sub">
          {BRAND.name} · 시행일 {EFFECTIVE_DATE}
        </p>
      </div>

      <p className="text-base leading-relaxed">
        새싹돋보기(이하 "서비스")는 보육기관과 가정을 잇는 소통·기록 서비스로서,
        「개인정보 보호법」 등 관계 법령을 준수하며 이용자의 개인정보를 소중히 다룹니다.
        이 방침은 서비스가 어떤 정보를 왜 수집하고, 어떻게 보관·보호하며, 언제 지우는지를
        설명합니다.
      </p>

      <Section title="1. 수집하는 개인정보와 수집 목적">
        <p>회원 가입과 서비스 이용 과정에서 아래 정보를 수집합니다.</p>
        <Table
          head={['구분', '항목', '수집 목적']}
          rows={[
            [
              '회원 가입 (필수)',
              '이메일, 비밀번호, 이름, 휴대전화번호',
              '회원 식별과 로그인, 기관·자녀 연결(초대코드), 본인 확인(문자 인증), 서비스 관련 안내',
            ],
            [
              '카카오 간편 로그인 (선택)',
              '카카오 회원번호, 카카오계정 이메일, 닉네임(이름)',
              '별도 비밀번호 없는 간편 가입·로그인, 기존 계정과의 연결',
            ],
            [
              '휴대폰 본인인증 (선택)',
              '이름, 생년월일, 휴대전화번호, CI(연계정보)',
              '법정 본인확인, 계정 명의 확인과 중복 가입 방지',
            ],
            [
              '서비스 이용 (역할에 따라)',
              '프로필 사진, 기기 푸시 토큰, 알림 수신 설정',
              '앱 알림 발송, 화면 표시',
            ],
            [
              '자동 수집',
              '접속 기록, 기기·브라우저 정보, 오류 기록',
              '서비스 안정 운영, 보안 사고 대응, 오류 개선',
            ],
          ]}
        />
        <p className="text-sm text-ink-sub">
          원아(아동)의 기록(알림장·사진·발달 기록 등)은 보육기관이 보호자 동의 아래 작성·관리하는
          정보이며, 서비스는 기관과의 계약에 따라 이를 안전하게 보관·처리하는 역할을 합니다.
          해당 기록의 열람 범위는 보호자 본인 자녀로 제한됩니다.
        </p>
      </Section>

      <Section title="2. 보유 및 이용 기간">
        <p>
          회원 정보는 <b>회원 탈퇴 시 지체 없이 파기</b>합니다. 다만 관계 법령에 따라 아래 정보는
          정해진 기간 동안 보관합니다.
        </p>
        <Table
          head={['항목', '기간', '근거']}
          rows={[
            ['계약·청약철회·대금결제 기록', '5년', '전자상거래법'],
            ['소비자 불만·분쟁 처리 기록', '3년', '전자상거래법'],
            ['접속 기록(로그)', '3개월', '통신비밀보호법'],
            [
              '원아 기록(재원 중 작성된 기록)',
              '퇴소 후 5년 이내(서류 유형별 상이)',
              '보육기관 기록 보존 관행·기관과의 계약',
            ],
          ]}
        />
      </Section>

      <Section title="3. 개인정보의 제3자 제공">
        <p>
          서비스는 이용자의 개인정보를 제3자에게 판매하거나 제공하지 않습니다. 다만 이용자가 속한
          보육기관의 구성원(원장·담임교사)은 서비스 이용에 필요한 범위에서 보호자·원아 정보를
          열람합니다. 법령에 따른 요청이 있는 경우는 예외로 합니다.
        </p>
      </Section>

      <Section title="4. 처리 위탁 및 국외 이전">
        <p>안정적인 서비스 제공을 위해 아래 업체에 처리를 위탁하며, 일부는 국외에 있습니다.</p>
        <Table
          head={['수탁 업체', '위탁 내용', '국가']}
          rows={[
            ['Railway Corp.', '서버·데이터베이스 호스팅', '미국'],
            ['Cloudflare, Inc.', '사진·파일 저장(비공개 저장소)', '미국'],
            ['Google LLC (Firebase)', '앱 푸시 알림 발송', '미국'],
            ['(주)누리고 (Solapi)', '문자·알림톡 발송', '대한민국'],
            ['㈜포트원 · ㈜KG이니시스', '휴대폰 본인인증 처리(본인확인기관 연계)', '대한민국'],
            [
              'Anthropic, PBC',
              'AI 글쓰기 도우미·육아 상담의 문장 생성(입력 텍스트 전송, 학습에 사용되지 않음)',
              '미국',
            ],
          ]}
        />
        <p className="text-sm text-ink-sub">
          사진은 비공개 저장소에 두고 짧은 시간만 유효한 열람 주소로만 전달합니다. AI 기능에는
          이름 등 식별 정보를 최소화한 텍스트만 전송합니다.
        </p>
      </Section>

      <Section title="5. 이용자의 권리">
        <p>
          이용자는 언제든지 본인의 개인정보를 열람·수정하거나 삭제(탈퇴)를 요청할 수 있고, 처리
          정지를 요구할 수 있습니다. 앱의 프로필 화면 또는 아래 연락처로 요청하시면 지체 없이
          처리합니다. 만 14세 미만 아동의 정보는 법정대리인이 권리를 행사합니다.
        </p>
      </Section>

      <Section title="6. 안전성 확보 조치">
        <ul className="list-disc pl-5">
          <li>비밀번호는 복호화할 수 없는 방식으로 암호화하여 저장합니다.</li>
          <li>모든 통신 구간을 암호화(TLS)합니다.</li>
          <li>기관 간 데이터는 서로 완전히 분리되어 다른 기관에서 접근할 수 없습니다.</li>
          <li>사진 원본 내려받기는 기본 차단되며, 접근 기록을 남깁니다.</li>
          <li>접근 권한을 역할(원장·교사·보호자)에 따라 최소한으로 부여합니다.</li>
        </ul>
      </Section>

      <Section title="7. 개인정보 보호책임자">
        <p>
          개인정보 처리에 관한 문의·불만·피해 구제는 아래로 연락해 주세요.
        </p>
        <ul className="list-disc pl-5">
          <li>개인정보 보호책임자: {officer?.name ?? '등록 절차 진행 중'}</li>
          {officer?.email && <li>이메일: {officer.email}</li>}
          {officer?.phone && <li>연락처: {officer.phone}</li>}
        </ul>
        <p className="text-sm text-ink-sub">
          기타 신고·상담: 개인정보침해 신고센터(privacy.kisa.or.kr, 국번 없이 118)
        </p>
      </Section>

      <Section title="8. 고지 의무">
        <p>
          이 방침의 내용이 바뀌면 시행 7일 전부터 서비스 공지사항으로 알립니다. 이 방침은{' '}
          {EFFECTIVE_DATE}부터 적용됩니다.
        </p>
      </Section>

      <p className="mt-10 text-center text-sm text-ink-sub">
        <Link to="/login" className="underline">
          로그인 화면으로
        </Link>
      </p>

      <SiteFooter />
    </main>
  );
}
